import { SessionHandler } from "verdi-web-client";

import {
    NOTES_CUSTOM_ERROR_PREFIX,
    NOTES_CUSTOM_WARNING_PREFIX,
    NOTES_DISABLED_DEVICE_TEXT,
    NOTES_SUPPRESS_WARNINGS_TEXT,
} from "@/services/map-management/icon-manager/core/notes";

/**
 * Owns which badge a v2-drawn marker shows. A source hands over the warnings and notes it read off
 * its own DTO and gets back the one badge to draw; nothing here asks what the marker is.
 *
 * Transitional duplicate: legacy resolves its own warnings through `makeWarnings`. Becomes the single
 * authority once legacy routes through it.
 *
 * Two of legacy's rules are absent; README "Known divergences" says which and why.
 */

export type MarkerWarning = {
    variant: "error" | "warning";
    text: string;
};

/** Only the fields the rules read, so any source's DTO can satisfy it */
type WarningLike = {
    warningLevel: number;
    warningTitle: string;
    warningTypePriority?: number;
};

const ERROR_LEVEL = 2;

/** The text legacy shows for a notes-driven warning, so both layers read identically */
const NOTES_WARNING_TEXT = "check notes";

/**
 * Notes are a legacy escape hatch — typing certain strings into a device's notes changes how it draws.
 * The strings are imported rather than restated so the two layers cannot disagree on them.
 */
const noteContains = (notes: string | undefined, marker: string) => Boolean(notes?.toLowerCase().includes(marker));

const SEVERITY_WEIGHT = 10_000;

/**
 * Legacy's ordering: severity first, ties broken on type priority.
 *
 * Legacy's own arithmetic yields NaN when `warningTypePriority` is absent, which silently disables its
 * tiebreaker. Defaulting to 0 keeps the intent without inheriting that.
 */
const severityScore = ({ warningLevel, warningTypePriority }: WarningLike) =>
    warningLevel * SEVERITY_WEIGHT + (warningTypePriority ?? 0);

/**
 * Whether the viewer has asked not to see device warnings at all.
 *
 * Lives next to the rule it feeds, but is called by `useMapMarkers` and reaches the resolver as
 * context. Applied with no role check despite the preference's name, matching legacy's `makeWarnings`.
 */
export const hasUserHiddenWarnings = () =>
    Boolean(SessionHandler.currentUserObject?.webPreferences?.hideRedDevicesOnAdmin);

type ResolveMarkerWarningParams = {
    warnings?: readonly WarningLike[];
    /** The device's own notes — not `adminNotes`, which these rules ignore */
    notes?: string;
    /** Required rather than defaulted, so a new source cannot quietly skip the viewer's preference */
    areWarningsHiddenByUser: boolean;
};

export const resolveMarkerWarning = ({
    warnings = [],
    notes,
    areWarningsHiddenByUser,
}: ResolveMarkerWarningParams): MarkerWarning | null => {
    if (
        areWarningsHiddenByUser ||
        noteContains(notes, NOTES_DISABLED_DEVICE_TEXT) ||
        noteContains(notes, NOTES_SUPPRESS_WARNINGS_TEXT)
    ) {
        return null;
    }

    // Level 0 is informational — notices like manual override, which shouldn't put a bubble on the map
    const actionable = warnings.filter((warning) => warning.warningLevel > 0);
    if (actionable.length > 0) {
        const worst = actionable.reduce((worstSoFar, warning) =>
            severityScore(warning) > severityScore(worstSoFar) ? warning : worstSoFar,
        );
        return {
            variant: worst.warningLevel >= ERROR_LEVEL ? "error" : "warning",
            text: worst.warningTitle,
        };
    }

    // Notes overrides rank below a real device warning, and `warning:` is tested first, so a note
    // carrying both reads as the lesser of the two. Both quirks are legacy's.
    if (noteContains(notes, NOTES_CUSTOM_WARNING_PREFIX)) {
        return { variant: "warning", text: NOTES_WARNING_TEXT };
    }
    if (noteContains(notes, NOTES_CUSTOM_ERROR_PREFIX)) {
        return { variant: "error", text: NOTES_WARNING_TEXT };
    }

    return null;
};
